/**
 * Reload that is guaranteed to fetch a fresh document.
 *
 * Plain `window.location.reload()` may re-serve the page from the bfcache or the
 * in-memory document that is already loaded, so the stale HTML (pointing at
 * now-404 hashed chunks) comes right back and the user sees "the reload button
 * does nothing". Navigating to a new URL (unique query param) via `replace()`
 * cannot be served from those caches, so the browser always re-fetches the
 * HTML — which nginx serves `no-store` — and gets the current asset hashes.
 */
export function hardReload(): void {
  try {
    const url = new URL(window.location.href);
    url.searchParams.set("_v", Date.now().toString());
    window.location.replace(url.toString());
  } catch {
    // URL/replace unavailable in some sandboxed contexts — fall back.
    window.location.reload();
  }
}

/** True for the "stale hashed chunk no longer exists on the server" errors. */
export function isChunkLoadError(message?: string | null): boolean {
  return (
    !!message &&
    (message.includes("Failed to fetch dynamically imported module") ||
      message.includes("Importing a module script failed") ||
      message.includes("error loading dynamically imported module"))
  );
}

// Session guard shared across all chunk-recovery paths (lazyWithReload,
// setupChunkErrorHandler, the error-boundary backstop) so they never
// double-reload within the same session.
export const CHUNK_RELOAD_KEY = "lytra_chunk_reload";

// Session guard for the version-update auto-reload. Keyed by the target
// version string (not a plain boolean) so a genuinely newer deploy after the
// first auto-reload can still trigger one more — while a rolling deploy that
// round-robins the client between old/new pods (or an edge cache still
// serving the pre-reload bundle) can't loop the reload forever: hardReload()
// navigates to a brand new page/JS context, so an in-memory flag alone
// wouldn't survive across that navigation.
export const VERSION_RELOAD_KEY = "lytra_version_reload";
